// WhatIsMySiloModal — the marketing moment for someone who arrived through
// SOMEONE ELSE'S silo. They tapped "Sign up" on a creator's public Q&A with no
// idea what the platform behind it is, so the signup sheet carries one small
// link — "What is My Silo?" — and this layer answers it.
//
// Deliberately a SIBLING layer, not a replacement: the signup form behind it
// never unmounts, so dismissing this returns them to whatever they had already
// typed. The alternative (bouncing them to /) would have thrown the form away.
//
// Oct 4, 2026 (Randy) — wears the marketing page's sunrise skin on purpose,
// down to the same photograph and the same amber: arriving here should feel
// like the marketing page walked into the silo, not like a second product.
// The use-case list is INLINED rather than opening UniqueWaysModal, because
// that modal is z-[90] and would paint underneath this one.
import { useState } from "react";
import { motion } from "framer-motion";
import { X, CaretDown, Sparkle, CaretRight } from "@phosphor-icons/react";
import { UNIQUE_WAYS } from "@/lib/waysToUse";

const LQIP_BG = "#0A0D0F";

const WaysList = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="mt-3 rounded-xl border border-[#F5F1E8]/14 bg-[#070B11]/25 backdrop-blur-2xl backdrop-brightness-[0.38] backdrop-saturate-150 divide-y divide-[#F5F1E8]/10 overflow-hidden" data-testid="what-is-ways-list">
      {UNIQUE_WAYS.map((w, i) => (
        <div key={w.title}>
          <button
            onClick={() => setOpenIdx((c) => (c === i ? null : i))}
            data-testid={`what-is-way-${i}`}
            className="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-[#F5F1E8]/[0.06] transition-colors"
          >
            <span className="text-sm text-[#F5F1E8]">{w.title}</span>
            <CaretDown
              size={14}
              className={`shrink-0 text-[#FFC24A] transition-transform ${openIdx === i ? "rotate-180" : ""}`}
            />
          </button>
          {openIdx === i && (
            <p className="px-4 pb-4 text-[13px] text-[#E7E2D6]/80 leading-relaxed whitespace-pre-line">
              {w.body}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default function WhatIsMySiloModal({ open, onClose }) {
  const [showWays, setShowWays] = useState(false);
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center p-3 sm:p-4"
      data-testid="what-is-my-silo-modal"
    >
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/70 backdrop-blur-[3px]"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 22, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="relative w-full max-w-xl max-h-[90dvh] rounded-2xl overflow-hidden border border-[#F5F1E8]/14 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.9)]"
        style={{ backgroundColor: LQIP_BG }}
      >
        {/* The photograph sits OUTSIDE the scroll container so it holds still
            while the copy moves over it — same trick the marketing hero uses. */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <img
            src="/brand/sunrise-1536.jpg"
            srcSet="/brand/sunrise-960.webp 960w, /brand/sunrise-1536.webp 1536w"
            sizes="640px"
            alt=""
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-[50%_42%] brightness-[0.78] saturate-[1.06]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#04070E]/75 via-[#050810]/86 to-[#07050A]/94" />
          <div className="absolute right-[-18%] top-[-10%] w-[60%] aspect-square rounded-full bg-[#FFB347]/12 blur-[90px]" />
        </div>

        <button
          onClick={onClose}
          data-testid="what-is-my-silo-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-[#05080C]/55 border border-[#F5F1E8]/15 text-[#F5F1E8]/80 hover:text-[#FFC24A] flex items-center justify-center backdrop-blur-sm"
        >
          <X size={16} />
        </button>

        <div
          className="relative overflow-y-auto max-h-[90dvh] px-6 sm:px-8 pt-7"
          style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        >
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#FFC24A]/90">
            <Sparkle size={13} weight="duotone" /> Original Intelligence (OI)
          </div>

          <h2 className="font-serif-display text-3xl sm:text-4xl text-[#F5F1E8] leading-[1.1] mt-4 [text-shadow:0_2px_30px_rgba(0,0,0,0.85)]">
            Everyone is making Artificial Intelligence smarter. Make yours…{" "}
            <span className="text-[#FFC24A]">Original.</span>
          </h2>

          <p className="mt-5 text-[15px] text-[#E7E2D6]/85 leading-relaxed">
            Every question typed into a general AI model makes <em className="text-[#FFC24A] not-italic font-medium">their</em> machine
            a little better. Nothing of it stays yours.
          </p>
          <p className="mt-3.5 text-[15px] text-[#E7E2D6]/85 leading-relaxed">
            My Silo inverts that. You pour in what you actually know — your videos, your writing,
            your teaching, your research, your beliefs, your business — and it becomes a{" "}
            <strong className="text-[#F5F1E8] font-medium">silo</strong>: an intelligence that knows
            only what you gave it, answers in your voice, and belongs to you. It never bleeds into
            anyone else's model.
          </p>
          <p className="mt-3.5 text-[15px] text-[#E7E2D6]/85 leading-relaxed">
            We don't think that's artificial. It's{" "}
            <strong className="text-[#FFC24A] font-medium">original intelligence</strong> — yours,
            grounded in something real.
          </p>

          <div className="mt-6 space-y-3">
            {[
              ["Feed it.", "Paste it, upload it, or point it at a YouTube channel. Documents, transcripts, notes, books, a decade of posts."],
              ["Shape it.", "It answers from your corpus and nothing else. Asked something outside its scope, it politely declines instead of inventing an answer."],
              ["Share it.", "A public Q&A page, a link in a comment, an icon on someone's home screen, a widget on your own website — or keep it entirely private."],
            ].map(([head, body]) => (
              <div key={head} className="flex gap-3">
                <CaretRight size={14} className="text-[#FFC24A] mt-1 shrink-0" />
                <p className="text-sm text-[#E7E2D6]/80 leading-relaxed">
                  <strong className="text-[#F5F1E8] font-medium">{head}</strong> {body}
                </p>
              </div>
            ))}
          </div>

          {/* `backdrop-brightness` rather than a flat tint — the same lesson the
              marketing hero records. A fixed tint that reads correctly over the
              forest leaves this card's type washed out where the sun sits;
              multiplying what is behind the glass darkens both by the same
              proportion, so it is legible wherever the card lands. */}
          <div className="mt-6 rounded-xl border border-[#F5F1E8]/14 bg-[#070B11]/25 backdrop-blur-2xl backdrop-brightness-[0.38] backdrop-saturate-150 p-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FFC24A]/80">
              A digital twin of your channel
            </div>
            <p className="mt-2.5 text-sm text-[#E7E2D6]/80 leading-relaxed">
              Point a silo at your YouTube library and it learns every video you've made. It can
              answer viewers in your voice, watch a new upload with you, and draft the reply you
              would actually have written.
            </p>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FFC24A]/80 mt-5">
              It doesn't have to be about you
            </div>
            <p className="mt-2.5 text-sm text-[#E7E2D6]/80 leading-relaxed">
              A church's library. A coaching program. A product manual. A family's recipes. Anything
              with a body of knowledge worth asking questions of.
            </p>
          </div>

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#FFC24A]/80">
              And if you want to go further
            </div>
            <p className="mt-2.5 text-sm text-[#E7E2D6]/80 leading-relaxed">
              Fans can follow and ask for free. When you're ready, you can let supporters subscribe
              or tip, and <strong className="text-[#F5F1E8] font-medium">Creator Pro</strong> opens
              the professional toolkit — your own website widget, deeper tooling, your own
              storefront. What comes of it is down to you and your audience; we don't make promises
              about income.
            </p>
          </div>

          <button
            onClick={() => setShowWays((v) => !v)}
            data-testid="what-is-my-silo-ways-toggle"
            className="mt-6 text-sm text-[#FFC24A] underline underline-offset-2 hover:text-[#F5F1E8] font-medium transition-colors inline-flex items-center gap-1.5"
          >
            {showWays ? "Hide the list" : `See ${UNIQUE_WAYS.length} ways people use My Silo`}
            <CaretDown size={13} className={`transition-transform ${showWays ? "rotate-180" : ""}`} />
          </button>
          {showWays && <WaysList />}

          <button
            onClick={onClose}
            data-testid="what-is-my-silo-back"
            className="mt-7 w-full rounded-full bg-[#F5EFE2] text-[#14110C] py-3 text-sm font-semibold hover:bg-[#FFC24A] transition-colors shadow-[0_10px_40px_-8px_rgba(255,194,74,0.5)]"
          >
            Back to sign up
          </button>
        </div>
      </motion.div>
    </div>
  );
}
